import {
  LocationSuggestionTypes,
  LocationVehicleCategory,
} from '@abg/shared/constants';
import { LocationSuggestion } from '@abg/typings';
import { LocationSessionOption } from '@abg/typings/biz/location.interface';

import { LocationSuggestionOption } from '../../location-search.types';

export const RECENT_LOCATIONS_LOCAL_STORAGE_KEY = 'recent-location-options';
export const MAX_RECENT_LOCATIONS_NUMBER = 2;

export const getParsedRecentOptions = (): LocationSessionOption[] | [] => {
  if (typeof globalThis === 'undefined' || !globalThis.localStorage) {
    return [];
  }

  const recentOptions = localStorage.getItem(
    RECENT_LOCATIONS_LOCAL_STORAGE_KEY,
  );

  if (!recentOptions) {
    return [];
  }

  const parsedRecentOptions = JSON.parse(
    recentOptions,
  ) as LocationSessionOption[];

  return parsedRecentOptions;
};

export const reorderRecentOptions = (
  parsedRecentOptions: [] | LocationSessionOption[],
  duplicatedNewOptionIndex: number,
): void => {
  const duplicatedNewOption = {
    ...parsedRecentOptions[duplicatedNewOptionIndex],
    timestamp: Date.now(),
  };

  const reorderedRecentOptions = [
    duplicatedNewOption,
    ...parsedRecentOptions.filter(
      (_, index) => index !== duplicatedNewOptionIndex,
    ),
  ];

  localStorage?.setItem(
    RECENT_LOCATIONS_LOCAL_STORAGE_KEY,
    JSON.stringify(reorderedRecentOptions),
  );
};

export const saveRecentOptions = (newValue: LocationSuggestionOption) => {
  const parsedRecentOptions = getParsedRecentOptions();

  if (!newValue?.identifier) return;

  const duplicatedNewOptionIndex = parsedRecentOptions.findIndex(
    ({ value }) => value?.identifier === newValue?.identifier,
  );
  const isDuplicatedNewOption = duplicatedNewOptionIndex !== -1;

  if (isDuplicatedNewOption) {
    reorderRecentOptions(parsedRecentOptions, duplicatedNewOptionIndex);
  } else {
    const filteredRecentOptions =
      parsedRecentOptions.length >= MAX_RECENT_LOCATIONS_NUMBER
        ? parsedRecentOptions
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 1)
        : parsedRecentOptions;

    const newRecentOption = {
      value: newValue,
      initialLocationSiteType: newValue.suggestionTypeCode,
      timestamp: Date.now(),
    };

    localStorage?.setItem(
      RECENT_LOCATIONS_LOCAL_STORAGE_KEY,
      JSON.stringify([newRecentOption, ...filteredRecentOptions]),
    );
  }
};

export const mapSearchSuggestionsOptions = (
  options: LocationSuggestionOption[],
) => {
  // Group neighborhood suggestion types under a single suggestion group
  const neighborhoodSuggestionGroup = LocationSuggestionTypes.NEIGHBOURHOOD;
  const neighborhoodTypes = new Set<string>([
    LocationSuggestionTypes.OTHER,
    LocationSuggestionTypes.CITY_DOWNTOWN,
    LocationSuggestionTypes.RAILWAY_BUS_STATION,
    LocationSuggestionTypes.NEIGHBOURHOOD,
  ]);

  return options.map((option) => {
    if (neighborhoodTypes.has(option.suggestionTypeCode)) {
      return {
        ...option,
        suggestionGroup: neighborhoodSuggestionGroup,
      };
    }

    return {
      ...option,
      suggestionGroup: option.suggestionTypeCode,
    };
  });
};

const excludeVehicleCategories =
  (excludeCategories?: LocationVehicleCategory[]) =>
  (value: LocationSuggestion | LocationSuggestionOption) => {
    return !excludeCategories?.includes(
      value.locationVehicleCategory as LocationVehicleCategory,
    );
  };

export const getRecentOptions = (
  excludeCategories?: LocationVehicleCategory[],
): LocationSuggestionOption[] | [] => {
  const parsedRecentOptions = getParsedRecentOptions();

  return parsedRecentOptions
    .filter((item) => {
      return excludeVehicleCategories(excludeCategories)(item.value);
    })
    .map(({ value }) => ({
      ...value,
      suggestionTypeCode: 'RECENT',
      suggestionGroup: 'RECENT',
    }));
};

export const getSavedOptions = (
  locations: LocationSuggestion[],
  excludeCategories?: LocationVehicleCategory[],
): LocationSuggestionOption[] | [] => {
  return locations
    .filter(excludeVehicleCategories(excludeCategories))
    .map((location) => ({
      ...location,
      suggestionGroup: 'SAVED',
    }));
};

export const getCurrentReturnOptions = (
  location: LocationSuggestionOption | LocationSuggestion,
): LocationSuggestionOption[] | [] => {
  return [location].map((location) => ({
    ...location,
    suggestionTypeCode: 'CURRENT_RESERVATION',
    suggestionGroup: 'CURRENT_RESERVATION',
  }));
};
